import React, { useEffect, useMemo, useState } from 'react';
import { PlusCircle } from 'react-feather';
import { AppInputField } from '../../components/general/AppInputField/AppInputField';
import { AppButton } from '../../components/general/AppButton/AppButton';
import { getAllStates, SupplierJobUpdate, SupplierJobAddressDetails } from '@dill/dill-shared';
import {
  PartialSupplierOwnerVerify,
  SupplierVerifiedJobFormData,
} from '../../components/general/AppNewSupplierJobModal/constants';
import VerifySupplierJob from './VerifySupplierJob';
import { AppCheckBox } from '../../components/general/AppCheckBox/AppCheckBox';
import { AppDropDown2 } from '../../components/general/AppDropDown2/AppDropDown2';
import AppFilePicker from '../../components/general/AppFilePicker/AppFilePicker';
import { COLORS } from '../../utils/colors';

type VerifiedJobDataPick = Pick<
  SupplierJobUpdate,
  | 'owners'
  | 'GCs'
  | 'lenders'
  | 'apn'
  | 'constructionMonitorPermitId'
  | 'estimatedMaterialAmount'
  | 'jobType'
  | 'jobSector'
  | 'customerType'
  | 'jobFurnishings'
  | 'hasNoLender'
  | 'jobAddress'
  | 'jobCity'
  | 'jobState'
  | 'jobZipCode'
  | 'jobCounty'
  | 'jobLegalDescription'
  | 'isJobBonded'
  | 'sureties'
>;

interface SupplierVerifyJobFormFunctions {
  supplierJobData: PartialSupplierOwnerVerify;
  onUseVerifiedData: (data: Record<string, any>, files?: File[]) => void;
  onManualDataUpdate: (data: Record<string, any>, files?: File[]) => void;
  onGoBack: () => void;
  saving: boolean;
  ownerVerificationEnable?: boolean;
}

enum ModalState {
  ADD_JOB,
  VERIFY_JOB,
  VERIFY_OWNER,
  VERIFY_OWNER_MANUAL,
}

type SupplierVerifyJobFormProps = SupplierVerifyJobFormFunctions & SupplierVerifiedJobFormData;

const VerifyJobForm = (props: SupplierVerifyJobFormProps) => {
  const {
    onUseVerifiedData,
    onManualDataUpdate,
    onGoBack,
    jobAddress,
    jobCity,
    jobState,
    jobZipCode,
    owners,
    verifiedProperty,
    jobNumber,
    name,
    buyerId,
    supplierJobData,
    constructionMonitorPermitId,
    estimatedMaterialAmount,
    jobType,
    jobSector,
    customerType,
    jobFurnishings,
    hasNoLender,
    GCs,
    saving,
    ownerVerificationEnable,
  } = props;

  const [modalState, setModalState] = useState<ModalState>(ModalState.VERIFY_OWNER);
  const [formState, setFormState] = useState<VerifiedJobDataPick>({});
  const [formAddressState, setAddressFormState] = useState<{
    GCs: SupplierJobAddressDetails[];
    owners: SupplierJobAddressDetails[];
    lenders?: SupplierJobAddressDetails[];
    sureties?: SupplierJobAddressDetails[];
  }>({
    GCs: [],
    owners: [],
  });
  const [showErrorAbove, setShowErrorAbove] = useState(false);
  const [ownerAndGCSame, setOwnerAndGCSame] = useState(false);
  const [formAddressError, setAddressError] = useState<{
    GCs: string;
    owners: string;
    lenders: string;
    sureties: string;
  }>({
    GCs: '',
    owners: '',
    lenders: '',
    sureties: '',
  });
  const [errors, setErrors] = useState<VerifiedJobDataPick>({});
  const [verifyJobData, setVerifyJobData] = useState<VerifiedJobDataPick>({});
  const [attachments, setAttachments] = useState<File[]>([]);

  const handleUpdateSupplierJobAddress = ({
    field,
    value,
    index,
    key,
  }: {
    field: keyof SupplierJobAddressDetails;
    value: string;
    index: number;
    key: 'GCs' | 'owners' | 'lenders' | 'sureties';
  }) => {
    setAddressFormState((prevState) => {
      const updated = [...(prevState[key] ?? [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prevState, [key]: updated };
    });
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
  };

  const handleAddAddress = (key: 'GCs' | 'owners' | 'lenders' | 'sureties') => {
    let moreFields = {};
    if (key === 'sureties') {
      moreFields = {
        bondNumber: '',
      };
    }
    setAddressFormState((prev) => ({
      ...prev,
      [key]: [
        ...(prev[key] ?? []),
        {
          name: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          ...moreFields,
        },
      ],
    }));
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
  };

  const handleRemoveAddress = (index: number, key: 'GCs' | 'owners' | 'lenders' | 'sureties') => {
    setAddressFormState((prev) => {
      const previousState = [...(prev[key] ?? [])];
      const updated = previousState.filter(
        (_: SupplierJobAddressDetails, i: number) => i !== index
      );
      return { ...prev, [key]: updated };
    });
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
  };

  const onChange2 = (data: { name: string; value: string | number | boolean }) => {
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
    setFormState((prevState) => ({
      ...prevState,
      [data.name]: data.value,
    }));
  };

  useEffect(() => {
    setVerifyJobData({
      jobAddress: jobAddress,
      jobCity: jobCity,
      jobState: jobState,
      jobZipCode: jobZipCode,
      owners: owners,
      GCs: GCs,
      jobType,
      apn: verifiedProperty?.apn ?? '',
    });
  }, [jobAddress, jobCity, jobState, jobZipCode, owners, GCs, jobType]);

  useEffect(() => {
    if (!ownerVerificationEnable) {
      setModalState(ModalState.VERIFY_OWNER_MANUAL);
    }
  }, []);

  const allStates = useMemo(() => {
    return getAllStates();
  }, []);

  const checkIfNotDataFound = useMemo(() => {
    return (
      verifyJobData.owners?.filter((item) => item.name === '').length !== 0 &&
      verifyJobData.GCs?.filter((item) => item.name === '').length !== 0 &&
      verifyJobData?.apn === ''
    );
  }, [verifyJobData]);

  const changeAutoState = () => {
    setModalState(ModalState.VERIFY_OWNER);
  };

  const changeManualState = () => {
    setModalState(ModalState.VERIFY_OWNER_MANUAL);
  };

  const validateInputs = () => {
    const newErrors = {} as VerifiedJobDataPick;

    let isValid = true;

    if (
      formState.estimatedMaterialAmount === '' ||
      Number(formState.estimatedMaterialAmount) <= 0
    ) {
      newErrors.estimatedMaterialAmount =
        formState.estimatedMaterialAmount === ''
          ? 'Estimated material amount is required.'
          : 'Estimated material amount should be more than 0.';
      isValid = false;
    }
    if (formState.jobFurnishings?.trim() === '') {
      newErrors.jobFurnishings = 'Job Furnishings is required.';
      isValid = false;
    }
    if (formState.jobState?.trim() === 'AZ' && formState.jobCounty?.trim() === '') {
      newErrors.jobCounty = 'Job county is required.';
      isValid = false;
    }
    if (formState.jobState?.trim() === 'AZ' && formState.jobLegalDescription?.trim() === '') {
      newErrors.jobLegalDescription = 'Job legal description is required.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateAddressInputs = () => {
    const newErrors = {} as {
      GCs: string;
      owners: string;
      lenders: string;
      sureties: string;
    };

    let isValid = true;

    if (formAddressState.GCs.length > 0) {
      formAddressState.GCs.some((gC) => {
        if (gC.name?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
        if (gC.address?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
        if (gC.city?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
        if (gC.state?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
        if (gC.zipCode?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
      });
    }

    if (formAddressState.owners.length > 0) {
      formAddressState.owners.some((owner) => {
        if (owner.name?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
        if (owner.address?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
        if (owner.city?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
        if (owner.state?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
        if (owner.zipCode?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
      });
    }

    if (formAddressState.lenders && formAddressState.lenders.length > 0 && !formState.hasNoLender) {
      formAddressState.lenders.some((lender) => {
        if (lender.name?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
        if (lender.address?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
        if (lender.city?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
        if (lender.state?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
        if (lender.zipCode?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
      });
    }

    if (
      formAddressState.sureties &&
      formAddressState.sureties.length > 0 &&
      formState.isJobBonded
    ) {
      formAddressState.sureties.some((surety) => {
        if (surety.name?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.address?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.city?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.state?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.zipCode?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.bondNumber?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
      });
    }

    setAddressError(newErrors);
    return isValid;
  };

  const onSubmitData = () => {
    validateInputs();
    validateAddressInputs();
    if (!validateInputs()) {
      setShowErrorAbove(true);
      return;
    }
    if (!validateAddressInputs()) {
      setShowErrorAbove(true);
      return;
    }
    setShowErrorAbove(false);

    const newFormStateData = {
      ...formState,
      ...formAddressState,
      lenders: formState.hasNoLender ? [] : formAddressState.lenders,
      sureties: formState.isJobBonded ? formAddressState.sureties : [],
    };
    if (modalState === ModalState.VERIFY_OWNER) {
      const updatedData = {
        ...newFormStateData,
        jobAddress: verifiedProperty?.address ? verifiedProperty.address : jobAddress,
        jobCity: verifiedProperty?.city ? verifiedProperty.city : jobCity,
        jobState: verifiedProperty?.state ? verifiedProperty.state : jobState,
        jobZipCode: verifiedProperty?.zipCode ? verifiedProperty.zipCode : jobZipCode,
        apn: verifiedProperty?.apn ? verifiedProperty.apn : '',
        constructionMonitorPermitId: constructionMonitorPermitId,
      };
      onUseVerifiedData(updatedData, attachments);
    } else if (modalState === ModalState.VERIFY_OWNER_MANUAL) {
      onManualDataUpdate(newFormStateData, attachments);
    }
  };

  useEffect(() => {
    if (modalState === ModalState.VERIFY_OWNER || modalState === ModalState.VERIFY_OWNER_MANUAL) {
      const addressObject = [
        {
          name: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
        },
      ];
      setAddressFormState((prevState) => {
        const ownerState =
          modalState === ModalState.VERIFY_OWNER
            ? owners || addressObject
            : owners || addressObject;
        const gcStates =
          modalState === ModalState.VERIFY_OWNER ? GCs || addressObject : addressObject;
        return { ...prevState, owners: ownerState, GCs: gcStates };
      });
      onChange2({
        name: 'apn',
        value: modalState === ModalState.VERIFY_OWNER ? verifiedProperty?.apn || '' : '',
      });
      onChange2({ name: 'jobNumber', value: jobNumber || '' });
      onChange2({ name: 'jobAddress', value: jobAddress || '' });
      onChange2({ name: 'jobCity', value: jobCity || '' });
      onChange2({ name: 'jobState', value: jobState || '' });
      onChange2({ name: 'jobZipCode', value: jobZipCode || '' });
      onChange2({ name: 'name', value: name || '' });
      onChange2({ name: 'buyerId', value: buyerId || '' });
      onChange2({
        name: 'estimatedMaterialAmount',
        value: formState.estimatedMaterialAmount || 0,
      });
      onChange2({ name: 'jobType', value: jobType || 'commercial' });
      onChange2({ name: 'jobSector', value: formState.jobSector || 'private' });
      onChange2({ name: 'customerType', value: formState.customerType || 'SUB' });
      onChange2({ name: 'jobFurnishings', value: formState.jobFurnishings || '' });
      onChange2({ name: 'hasNoLender', value: formState.hasNoLender || false });
      onChange2({ name: 'isJobBonded', value: formState.isJobBonded || false });
      if (jobState === 'AZ') {
        onChange2({ name: 'jobCounty', value: formState.jobCounty || '' });
        onChange2({ name: 'jobLegalDescription', value: formState.jobLegalDescription || '' });
      }
    }
  }, [modalState]);

  useEffect(() => {
    if (supplierJobData) {
      let moreDetails = {};
      if (supplierJobData.jobState === 'AZ') {
        moreDetails = {
          jobCounty: supplierJobData.jobCounty ?? '',
          jobLegalDescription: supplierJobData.jobLegalDescription ?? '',
        };
      }
      setFormState({
        apn: supplierJobData.apn,
        estimatedMaterialAmount: supplierJobData.estimatedMaterialAmount,
        jobType: supplierJobData.jobType,
        jobSector: supplierJobData.jobSector,
        customerType: supplierJobData.customerType,
        jobFurnishings: supplierJobData.jobFurnishings,
        hasNoLender: supplierJobData.hasNoLender,
        isJobBonded: supplierJobData.isJobBonded,
        ...moreDetails,
      });
      const addressData = [
        {
          name: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
        },
      ];
      setAddressFormState({
        GCs:
          supplierJobData.GCs && supplierJobData.GCs.length > 0 ? supplierJobData.GCs : addressData,
        owners:
          supplierJobData.owners && supplierJobData.owners.length > 0
            ? supplierJobData.owners
            : addressData,
        lenders:
          supplierJobData.lenders && supplierJobData.lenders.length > 0
            ? supplierJobData.lenders
            : addressData,
        sureties:
          supplierJobData.sureties && supplierJobData.sureties.length > 0
            ? supplierJobData.sureties
            : addressData,
      });
    }
  }, [supplierJobData]);

  const handleOwnerAndGCSame = (value: boolean) => {
    if (value) {
      const data = {
        owners:
          formAddressState.owners &&
          formAddressState.owners?.length > 0 &&
          formAddressState.owners[0].name !== ''
            ? formAddressState.owners
            : formAddressState.GCs,
        GCs:
          formAddressState.GCs &&
          formAddressState.GCs?.length > 0 &&
          formAddressState.GCs[0].name !== ''
            ? formAddressState.GCs
            : formAddressState.owners,
      };

      setAddressFormState((prevState) => ({
        ...prevState,
        ...data,
      }));
    }
    setOwnerAndGCSame(value);
  };

  return (
    <div className="WHITE-BG w-full mt-4 rounded-lg">
      <div className="flex flex-col w-full">
        <VerifySupplierJob
          verifyJobData={verifyJobData}
          ownerVerificationEnable={ownerVerificationEnable}
        />
        {(modalState === ModalState.VERIFY_OWNER ||
          modalState === ModalState.VERIFY_OWNER_MANUAL) && (
          <div className="px-6">
            <div className="pb-3">
              <div className="text-xs mb-1 font-semibold">Job Type</div>
              <div className="flex flex-row gap-3">
                <div className="flex  items-center">
                  <div
                    className="flex items-center mr-1 cursor-pointer"
                    onClick={() => onChange2({ name: 'jobType', value: 'commercial' })}>
                    <div
                      className={`flex items-center justify-center h-4 w-4 border-2 rounded-full ${
                        formState.jobType === 'commercial'
                          ? 'PRIMARY_500-BORDER'
                          : 'GREY_500-BORDER'
                      }`}>
                      <div
                        className={`flex items-center h-2 w-2 rounded-full ${
                          formState.jobType === 'commercial' ? 'PRIMARY_500-BG ' : ''
                        }`}></div>
                    </div>
                  </div>
                  <div className="text-xs">Commercial</div>
                </div>
                <div className="flex  items-center">
                  <div
                    className="flex items-center mr-1 cursor-pointer"
                    onClick={() => onChange2({ name: 'jobType', value: 'residential' })}>
                    <div
                      className={`flex items-center justify-center h-4 w-4 border-2 rounded-full ${
                        formState.jobType === 'residential'
                          ? 'PRIMARY_500-BORDER'
                          : 'GREY_500-BORDER'
                      }`}>
                      <div
                        className={`flex items-center h-2 w-2 rounded-full ${
                          formState.jobType === 'residential' ? 'PRIMARY_500-BG ' : ''
                        }`}></div>
                    </div>
                  </div>
                  <div className="text-xs">Residential</div>
                </div>
              </div>
            </div>
            <div className="pb-3">
              <div className="text-xs mb-1 font-semibold">Job Sector</div>
              <div className="flex flex-row gap-3">
                <div className="flex  items-center">
                  <div
                    className="flex items-center mr-1 cursor-pointer"
                    onClick={() => onChange2({ name: 'jobSector', value: 'private' })}>
                    <div
                      className={`flex items-center justify-center h-4 w-4 border-2 rounded-full ${
                        formState.jobSector === 'private' ? 'PRIMARY_500-BORDER' : 'GREY_500-BORDER'
                      }`}>
                      <div
                        className={`flex items-center h-2 w-2 rounded-full ${
                          formState.jobSector === 'private' ? 'PRIMARY_500-BG ' : ''
                        }`}></div>
                    </div>
                  </div>
                  <div className="text-xs">Private</div>
                </div>
                <div className="flex  items-center">
                  <div
                    className="flex items-center mr-1 cursor-pointer"
                    onClick={() => onChange2({ name: 'jobSector', value: 'public' })}>
                    <div
                      className={`flex items-center justify-center h-4 w-4 border-2 rounded-full ${
                        formState.jobSector === 'public' ? 'PRIMARY_500-BORDER' : 'GREY_500-BORDER'
                      }`}>
                      <div
                        className={`flex items-center h-2 w-2 rounded-full ${
                          formState.jobSector === 'public' ? 'PRIMARY_500-BG ' : ''
                        }`}></div>
                    </div>
                  </div>
                  <div className="text-xs">Public</div>
                </div>
              </div>
            </div>
            <div className="flex flex-row  pb-3">
              <div className="w-full">
                <AppInputField
                  id="jobFurnishings"
                  label="Job Furnishings"
                  placeholder="Job Furnishings"
                  isRequired={true}
                  value={formState.jobFurnishings}
                  errorText={errors.jobFurnishings ? `${errors.jobFurnishings}` : ''}
                  onTextChange={(text) => {
                    onChange2({ name: 'jobFurnishings', value: text });
                  }}
                />
              </div>
            </div>
            <div className="flex flex-row  pb-3">
              <div className="w-full">
                <AppInputField
                  id="estimatedMaterialAmount"
                  label="Estimated Material Amount"
                  placeholder="12345"
                  inputType="number"
                  isRequired={true}
                  value={formState.estimatedMaterialAmount}
                  errorText={
                    errors.estimatedMaterialAmount ? `${errors.estimatedMaterialAmount}` : ''
                  }
                  onTextChange={(text) => {
                    onChange2({ name: 'estimatedMaterialAmount', value: text });
                  }}
                />
              </div>
            </div>
            {formState.jobState === 'AZ' && (
              <div className="flex flex-row  pb-3">
                <div className="w-full">
                  <AppInputField
                    id="jobCounty"
                    label="Job County"
                    isRequired={true}
                    placeholder="Job County"
                    value={formState.jobCounty}
                    errorText={errors.jobCounty ? `${errors.jobCounty}` : ''}
                    onTextChange={(text) => {
                      onChange2({ name: 'jobCounty', value: text });
                    }}
                  />
                </div>
              </div>
            )}
            {formState.jobState === 'AZ' && (
              <div className="flex flex-row  pb-3">
                <div className="w-full">
                  <AppInputField
                    id="jobLegalDescription"
                    isRequired={true}
                    label="Job Legal Description"
                    placeholder="Job Legal Description"
                    value={formState.jobLegalDescription}
                    errorText={errors.jobLegalDescription ? `${errors.jobLegalDescription}` : ''}
                    onTextChange={(text) => {
                      onChange2({ name: 'jobLegalDescription', value: text });
                    }}
                  />
                </div>
              </div>
            )}
            <div className="flex flex-row  pb-3">
              <div className="w-full">
                <AppInputField
                  id="apn"
                  label="Property APN"
                  placeholder="12345"
                  isRequired={false}
                  value={formState.apn}
                  errorText={errors.apn ? errors.apn : ''}
                  onTextChange={(text) => {
                    onChange2({ name: 'apn', value: text });
                  }}
                />
              </div>
            </div>
            <div className="flex w-full pb-3">
              <AppCheckBox
                className="mr-2"
                isChecked={formState.customerType === 'GC'}
                onClick={(val: boolean) => {
                  onChange2({ name: 'customerType', value: val ? 'GC' : 'SUB' });
                }}
              />
              <div className="text-black text-sm">
                You are the general contractor on the project, not a subcontractor.
              </div>
            </div>
            <div className="bg-slate-100 px-2 py-2 rounded-lg my-4">
              <div className="text-sm font-semibold">GC</div>
              {formAddressState.GCs.map((entry, index) => {
                return (
                  <div
                    key={`gc-address-input-${index}`}
                    className="border-b GREY_300-BORDER pb-2 mb-1">
                    {formAddressState.GCs.length > 1 && (
                      <div className="w-full flex justify-end">
                        <button
                          type="button"
                          className={'text-red-500 text-sm self-start'}
                          onClick={() => handleRemoveAddress(index, 'GCs')}>
                          Remove
                        </button>
                      </div>
                    )}
                    <div className="flex flex-row  pb-3">
                      <div className="w-full relative">
                        <AppInputField
                          id={`gc-${index}`}
                          label="GC Name"
                          inputType="text"
                          placeholder="GC Name"
                          isRequired={true}
                          value={entry.name}
                          errorText={
                            formAddressError.GCs !== '' && entry.name === ''
                              ? 'GC Name is required'
                              : ''
                          }
                          onTextChange={(text) => {
                            handleUpdateSupplierJobAddress({
                              value: text,
                              key: 'GCs',
                              field: 'name',
                              index: index,
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex flex-row  pb-3">
                      <div className="w-full ">
                        <AppInputField
                          id={`gc-address-${index}`}
                          label="GC Address"
                          inputType="text"
                          placeholder="GC Address"
                          isRequired={true}
                          value={entry.address}
                          errorText={
                            formAddressError.GCs !== '' && entry.address === ''
                              ? 'GC Address is required'
                              : ''
                          }
                          onTextChange={(text) => {
                            handleUpdateSupplierJobAddress({
                              value: text,
                              key: 'GCs',
                              field: 'address',
                              index: index,
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex flex-row w-full gap-1">
                      <AppInputField
                        id={`gc-city-${index}`}
                        label="GC City"
                        inputType="text"
                        placeholder="GC City"
                        isRequired={true}
                        value={entry.city}
                        errorText={
                          formAddressError.GCs !== '' && entry.city === ''
                            ? 'GC City is required'
                            : ''
                        }
                        onTextChange={(text) => {
                          handleUpdateSupplierJobAddress({
                            value: text,
                            key: 'GCs',
                            field: 'city',
                            index: index,
                          });
                        }}
                      />
                      <div className="w-full">
                        <AppDropDown2
                          isRequired
                          label="GC State"
                          value={entry.state || null}
                          errorText={
                            formAddressError.GCs !== '' && entry.state === ''
                              ? 'GC State is required'
                              : ''
                          }
                          items={[
                            ...Object.keys(allStates).map((stateName) => {
                              const key = stateName as keyof typeof allStates;
                              const stateKey = allStates[key];
                              return {
                                label: `${stateKey} - ${stateName}`,
                                value: stateKey,
                              };
                            }),
                          ]}
                          onSelectChange={(item) => {
                            if (item && item?.value !== null) {
                              const foundState = Object.values(getAllStates()).find(
                                (stateName) => stateName === item?.value
                              );
                              if (foundState) {
                                handleUpdateSupplierJobAddress({
                                  value: item.value,
                                  key: 'GCs',
                                  field: 'state',
                                  index: index,
                                });
                              }
                            }
                          }}
                        />
                      </div>
                      <AppInputField
                        id={`gc-zip-${index}`}
                        label="GC Zip Code"
                        inputType="text"
                        placeholder="12345"
                        isRequired={true}
                        value={entry.zipCode}
                        errorText={
                          formAddressError.GCs !== '' && entry.zipCode === ''
                            ? 'GC Zip Code is required'
                            : ''
                        }
                        onTextChange={(text) => {
                          handleUpdateSupplierJobAddress({
                            value: text,
                            key: 'GCs',
                            field: 'zipCode',
                            index: index,
                          });
                        }}
                      />
                    </div>
                  </div>
                );
              })}
              <div className="w-full flex mb-2 justify-end">
                <AppButton
                  text="Add GC"
                  onClick={() => handleAddAddress('GCs')}
                  icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                  buttonStyles={{
                    height: '30px',
                    borderRadius: '5px',
                  }}
                />
              </div>
            </div>
            <div className="flex w-full pb-3">
              <AppCheckBox
                className="mr-2"
                isChecked={ownerAndGCSame}
                onClick={handleOwnerAndGCSame}
              />
              <div className="text-black text-sm">Owner and GC are the same.</div>
            </div>
            <div className="bg-slate-100 px-2 py-2 rounded-lg my-4">
              <div className="text-sm font-semibold">Owner</div>
              {formAddressState.owners.map((entry, index) => {
                return (
                  <div
                    key={`owner-address-input-${index}`}
                    className="border-b GREY_300-BORDER pb-2 mb-1">
                    {formAddressState.owners.length > 1 && (
                      <div className="w-full flex justify-end">
                        <button
                          type="button"
                          className={'text-red-500 text-sm self-start'}
                          onClick={() => handleRemoveAddress(index, 'owners')}>
                          Remove
                        </button>
                      </div>
                    )}
                    <div className="flex flex-row  pb-3">
                      <div className="w-full relative">
                        <AppInputField
                          id={`owner-${index}`}
                          label="Owner Name"
                          inputType="text"
                          placeholder="Owner Name"
                          isRequired={true}
                          value={entry.name}
                          errorText={
                            formAddressError.owners !== '' && entry.name === ''
                              ? 'Owner Name is required'
                              : ''
                          }
                          onTextChange={(text) => {
                            handleUpdateSupplierJobAddress({
                              value: text,
                              key: 'owners',
                              field: 'name',
                              index: index,
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex flex-row  pb-3">
                      <div className="w-full ">
                        <AppInputField
                          id={`owner-address-${index}`}
                          label="Owner Address"
                          inputType="text"
                          placeholder="Owner Address"
                          isRequired={true}
                          value={entry.address}
                          errorText={
                            formAddressError.owners !== '' && entry.address === ''
                              ? 'Owner Address is required'
                              : ''
                          }
                          onTextChange={(text) => {
                            handleUpdateSupplierJobAddress({
                              value: text,
                              key: 'owners',
                              field: 'address',
                              index: index,
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex flex-row w-full gap-1">
                      <AppInputField
                        id={`owner-city-${index}`}
                        label="Owner City"
                        inputType="text"
                        placeholder="Owner City"
                        isRequired={true}
                        value={entry.city}
                        errorText={
                          formAddressError.owners !== '' && entry.city === ''
                            ? 'Owner City is required'
                            : ''
                        }
                        onTextChange={(text) => {
                          handleUpdateSupplierJobAddress({
                            value: text,
                            key: 'owners',
                            field: 'city',
                            index: index,
                          });
                        }}
                      />
                      <div className="w-full">
                        <AppDropDown2
                          isRequired
                          label="Owner State"
                          value={entry.state || null}
                          errorText={
                            formAddressError.owners !== '' && entry.state === ''
                              ? 'Owner State is required'
                              : ''
                          }
                          items={[
                            ...Object.keys(allStates).map((stateName) => {
                              const key = stateName as keyof typeof allStates;
                              const stateKey = allStates[key];
                              return {
                                label: `${stateKey} - ${stateName}`,
                                value: stateKey,
                              };
                            }),
                          ]}
                          onSelectChange={(item) => {
                            if (item && item?.value !== null) {
                              const foundState = Object.values(getAllStates()).find(
                                (stateName) => stateName === item?.value
                              );
                              if (foundState) {
                                handleUpdateSupplierJobAddress({
                                  value: item.value,
                                  key: 'owners',
                                  field: 'state',
                                  index: index,
                                });
                              }
                            }
                          }}
                        />
                      </div>
                      <AppInputField
                        id={`owner-zip-${index}`}
                        label="Owner Zip Code"
                        inputType="text"
                        placeholder="12345"
                        isRequired={true}
                        value={entry.zipCode}
                        errorText={
                          formAddressError.owners !== '' && entry.zipCode === ''
                            ? 'Owner Zip Code is required'
                            : ''
                        }
                        onTextChange={(text) => {
                          handleUpdateSupplierJobAddress({
                            value: text,
                            key: 'owners',
                            field: 'zipCode',
                            index: index,
                          });
                        }}
                      />
                    </div>
                  </div>
                );
              })}
              <div className="w-full flex mb-2 justify-end">
                <AppButton
                  text="Add Owner"
                  onClick={() => handleAddAddress('owners')}
                  icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                  buttonStyles={{
                    height: '30px',
                    borderRadius: '5px',
                  }}
                />
              </div>
            </div>
            <div className="flex w-full pb-3 items-center">
              <AppCheckBox
                className="mr-2"
                isChecked={formState.hasNoLender}
                onClick={(val: boolean) => {
                  onChange2({ name: 'hasNoLender', value: val });
                }}
              />
              <div className="text-black text-sm">There is no lender on this job</div>
            </div>
            {!formState.hasNoLender && (
              <div>
                <div className="bg-slate-100 px-2 py-2 rounded-lg my-4">
                  <div className="text-sm font-semibold">Lender</div>
                  {formAddressState.lenders &&
                    formAddressState.lenders.map((entry, index) => (
                      <div
                        key={`lender-address-input-${index}`}
                        className="border-b GREY_300-BORDER pb-2 mb-1">
                        {formAddressState.lenders && formAddressState.lenders.length > 1 && (
                          <div className="w-full flex justify-end">
                            <button
                              type="button"
                              className={'text-red-500 text-sm self-start'}
                              onClick={() => handleRemoveAddress(index, 'lenders')}>
                              Remove
                            </button>
                          </div>
                        )}
                        <div className="flex flex-row  pb-3">
                          <div className="w-full">
                            <AppInputField
                              id={`lender-${index}`}
                              label="Lender Name"
                              inputType="text"
                              placeholder="Lender Name"
                              isRequired={!formState.hasNoLender}
                              value={entry.name}
                              errorText={
                                formAddressError.lenders !== '' && entry.name === ''
                                  ? 'Lender Name is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'lenders',
                                  field: 'name',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-row  pb-3">
                          <div className="w-full ">
                            <AppInputField
                              id={`lender-address-${index}`}
                              label="Lender Address"
                              inputType="text"
                              placeholder="Lender Address"
                              isRequired={!formState.hasNoLender}
                              value={entry.address}
                              errorText={
                                formAddressError.lenders !== '' && entry.address === ''
                                  ? 'Lender Address is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'lenders',
                                  field: 'address',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-row  pb-3 gap-1">
                          <AppInputField
                            id={`lender-city-${index}`}
                            label="Lender City"
                            inputType="text"
                            placeholder="Lender City"
                            isRequired={!formState.hasNoLender}
                            value={entry.city}
                            errorText={
                              formAddressError.lenders !== '' && entry.city === ''
                                ? 'Lender City is required'
                                : ''
                            }
                            onTextChange={(text) => {
                              handleUpdateSupplierJobAddress({
                                value: text,
                                key: 'lenders',
                                field: 'city',
                                index: index,
                              });
                            }}
                          />
                          <AppDropDown2
                            isRequired
                            label="Lender State"
                            value={entry.state || null}
                            errorText={
                              formAddressError.lenders !== '' && entry.state === ''
                                ? 'Lender State is required'
                                : ''
                            }
                            items={[
                              ...Object.keys(allStates).map((stateName) => {
                                const key = stateName as keyof typeof allStates;
                                const stateKey = allStates[key];
                                return {
                                  label: `${stateKey} - ${stateName}`,
                                  value: stateKey,
                                };
                              }),
                            ]}
                            onSelectChange={(item) => {
                              if (item && item?.value !== null) {
                                const foundState = Object.values(getAllStates()).find(
                                  (stateName) => stateName === item?.value
                                );
                                if (foundState) {
                                  handleUpdateSupplierJobAddress({
                                    value: item.value,
                                    key: 'lenders',
                                    field: 'state',
                                    index: index,
                                  });
                                }
                              }
                            }}
                          />
                          <AppInputField
                            id={`lender-zip-${index}`}
                            label="Lender Zip"
                            inputType="text"
                            placeholder="Lender Zip"
                            isRequired={!formState.hasNoLender}
                            value={entry.zipCode}
                            errorText={
                              formAddressError.lenders !== '' && entry.zipCode === ''
                                ? 'Lender Zip Code is required'
                                : ''
                            }
                            onTextChange={(text) => {
                              handleUpdateSupplierJobAddress({
                                value: text,
                                key: 'lenders',
                                field: 'zipCode',
                                index: index,
                              });
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  <div className="w-full flex mb-2 justify-end">
                    <AppButton
                      text="Add Lender"
                      onClick={() => handleAddAddress('lenders')}
                      icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                      buttonStyles={{
                        height: '30px',
                        borderRadius: '5px',
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="flex w-full pb-3 items-center">
              <AppCheckBox
                className="mr-2"
                isChecked={formState.isJobBonded}
                onClick={(val: boolean) => {
                  onChange2({ name: 'isJobBonded', value: val });
                }}
              />
              <div className="text-black text-sm">Is this a bonded job?</div>
            </div>
            {formState.isJobBonded && (
              <div>
                <div className="bg-slate-100 px-2 py-2 rounded-lg my-4">
                  <div className="text-sm font-semibold">Surety</div>
                  {formAddressState.sureties &&
                    formAddressState.sureties.map((entry, index) => (
                      <div
                        key={`sureties-address-${index}`}
                        className="border-b GREY_300-BORDER pb-2 mb-1">
                        {formAddressState.sureties && formAddressState.sureties.length > 1 && (
                          <div className="w-full flex justify-end">
                            <button
                              type="button"
                              className={'text-red-500 text-sm self-start'}
                              onClick={() => handleRemoveAddress(index, 'sureties')}>
                              Remove
                            </button>
                          </div>
                        )}
                        <div className="flex flex-row  pb-3">
                          <div className="w-full">
                            <AppInputField
                              id={`surety-${index}`}
                              label="Surety"
                              inputType="text"
                              placeholder="Surety"
                              isRequired={true}
                              value={entry.name}
                              errorText={
                                formAddressError.sureties !== '' && entry.name === ''
                                  ? 'Surety Name is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'sureties',
                                  field: 'name',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-row  pb-3">
                          <div className="w-full">
                            <AppInputField
                              id={`suretyAddress-${index}`}
                              label="Surety Address"
                              inputType="text"
                              placeholder="Surety Address"
                              isRequired={true}
                              value={entry.address}
                              errorText={
                                formAddressError.sureties !== '' && entry.address === ''
                                  ? 'Surety Address is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'sureties',
                                  field: 'address',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-row  pb-3 gap-2">
                          <div className="w-full">
                            <AppInputField
                              id={`suretyCity-${index}`}
                              label="Surety City"
                              inputType="text"
                              placeholder="Surety City"
                              isRequired={true}
                              value={entry.city}
                              errorText={
                                formAddressError.sureties !== '' && entry.city === ''
                                  ? 'Surety City is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'sureties',
                                  field: 'city',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                          <div className="w-full">
                            <AppDropDown2
                              label="Surety State"
                              isRequired={true}
                              value={entry.state || null}
                              errorText={
                                formAddressError.sureties !== '' && entry.state === ''
                                  ? 'Surety State is required'
                                  : ''
                              }
                              items={[
                                ...Object.keys(allStates).map((stateName) => {
                                  const key = stateName as keyof typeof allStates;
                                  const stateKey = allStates[key];
                                  return {
                                    label: `${stateKey} - ${stateName}`,
                                    value: stateKey,
                                  };
                                }),
                              ]}
                              onSelectChange={(item) => {
                                if (item && item?.value !== null) {
                                  const foundState = Object.values(getAllStates()).find(
                                    (stateName) => stateName === item?.value
                                  );
                                  if (foundState) {
                                    handleUpdateSupplierJobAddress({
                                      value: item.value,
                                      key: 'sureties',
                                      field: 'state',
                                      index: index,
                                    });
                                  }
                                }
                              }}
                            />
                          </div>
                          <div className="w-full">
                            <AppInputField
                              id={`suretyZip-${index}`}
                              label="Surety Zip"
                              inputType="text"
                              placeholder="Surety Zip"
                              isRequired={true}
                              value={entry.zipCode}
                              errorText={
                                formAddressError.sureties !== '' && entry.zipCode === ''
                                  ? 'Surety Zip Code is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'sureties',
                                  field: 'zipCode',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-row  pb-3">
                          <div className="w-full">
                            <AppInputField
                              id="bondNumber"
                              label="Bond Number"
                              inputType="text"
                              placeholder="Bond Number"
                              isRequired={true}
                              value={entry.bondNumber}
                              errorText={
                                formAddressError.sureties !== '' && entry.bondNumber === ''
                                  ? 'Bond Number is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'sureties',
                                  field: 'bondNumber',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  <div className="w-full flex mb-2 justify-end">
                    <AppButton
                      text="Add Surety"
                      onClick={() => handleAddAddress('sureties')}
                      icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                      buttonStyles={{
                        height: '30px',
                        borderRadius: '5px',
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="pb-3">
              <div className="text-xs mb-0.5  whitespace-nowrap font-medium">Attachments</div>
              <AppFilePicker
                onSelectFile={(files: File[]) => {
                  setAttachments(files);
                }}
              />
            </div>
          </div>
        )}
      </div>
      <hr className="h-px my-2 bg-gray-200 border-0 dark:bg-gray-200"></hr>
      {showErrorAbove && (
        <div className="my-1 text-xs ERROR_500-CLR text-center">Please see errors above</div>
      )}
      <div className="flex px-4 py-2 w-full">
        {modalState === ModalState.VERIFY_JOB && (
          <div className="flex w-full justify-between">
            <div>
              <AppButton
                text="Back"
                type="TERTIARY"
                onClick={onGoBack}
                buttonStyles={{
                  marginTop: '4px',
                  marginBottom: '4px',
                  height: '40px',
                }}
              />
            </div>
            <div className="flex flex-row gap-2">
              <AppButton
                text="Manually update information"
                type="SECONDARY"
                onClick={changeManualState}
                buttonStyles={{
                  marginTop: '4px',
                  marginBottom: '4px',
                  height: '40px',
                }}
              />
              {!checkIfNotDataFound && (
                <AppButton
                  text="Use this data"
                  onClick={changeAutoState}
                  buttonStyles={{
                    marginTop: '4px',
                    marginBottom: '4px',
                    height: '40px',
                  }}
                />
              )}
            </div>
          </div>
        )}
        {(modalState === ModalState.VERIFY_OWNER ||
          modalState === ModalState.VERIFY_OWNER_MANUAL) && (
          <div className="flex flex-row-reverse px-4 py-2 w-full gap-2">
            <AppButton
              text="Submit"
              type="PRIMARY"
              onClick={onSubmitData}
              buttonStyles={{
                marginTop: '4px',
                marginBottom: '4px',
                height: '40px',
                width: '100px',
              }}
              isDisabled={saving}
              isLoading={saving}
            />
            <AppButton
              text="Back"
              type="TERTIARY"
              onClick={onGoBack}
              buttonStyles={{
                marginTop: '4px',
                marginBottom: '4px',
                height: '40px',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyJobForm;
